import { useEffect, useState } from "react";
import { getDepartments, getNafSections } from "@/api/references";
import type { Department, NafSection } from "@/api/types";

interface CacheEntry<T> {
  promise: Promise<T[]>;
  value: T[] | null;
}

const departmentsCache = new Map<string, CacheEntry<Department>>();
const nafCache = new Map<string, CacheEntry<NafSection>>();
const subscribers = new Map<string, Set<() => void>>();

function subKey(kind: "dep" | "naf", country: string): string {
  return `${kind}:${country}`;
}

function subscribe(key: string, fn: () => void): () => void {
  let set = subscribers.get(key);
  if (!set) {
    set = new Set();
    subscribers.set(key, set);
  }
  set.add(fn);
  return () => {
    set?.delete(fn);
  };
}

function notify(key: string) {
  subscribers.get(key)?.forEach((fn) => fn());
}

function loadDepartments(country: string): Promise<Department[]> {
  const existing = departmentsCache.get(country);
  if (existing) return existing.promise;
  const entry: CacheEntry<Department> = {
    value: null,
    promise: getDepartments(country)
      .then((res) => {
        entry.value = res.departments;
        notify(subKey("dep", country));
        return res.departments;
      })
      .catch(() => {
        entry.value = [];
        notify(subKey("dep", country));
        return [];
      }),
  };
  departmentsCache.set(country, entry);
  return entry.promise;
}

function loadNafSections(country: string): Promise<NafSection[]> {
  const existing = nafCache.get(country);
  if (existing) return existing.promise;
  const entry: CacheEntry<NafSection> = {
    value: null,
    promise: getNafSections(country)
      .then((res) => {
        entry.value = res.sections;
        notify(subKey("naf", country));
        return res.sections;
      })
      .catch(() => {
        entry.value = [];
        notify(subKey("naf", country));
        return [];
      }),
  };
  nafCache.set(country, entry);
  return entry.promise;
}

export function useDepartments(country: string = "FR"): {
  departments: Department[];
  loading: boolean;
} {
  const [, force] = useState(0);
  useEffect(() => {
    void loadDepartments(country);
    return subscribe(subKey("dep", country), () =>
      force((n) => n + 1),
    );
  }, [country]);
  const entry = departmentsCache.get(country);
  return {
    departments: entry?.value ?? [],
    loading: entry?.value == null,
  };
}

export function useNafSections(country: string = "FR"): {
  sections: NafSection[];
  loading: boolean;
} {
  const [, force] = useState(0);
  useEffect(() => {
    void loadNafSections(country);
    return subscribe(subKey("naf", country), () =>
      force((n) => n + 1),
    );
  }, [country]);
  const entry = nafCache.get(country);
  return {
    sections: entry?.value ?? [],
    loading: entry?.value == null,
  };
}
